import { useCampContext } from "~/contexts/CampProvider"
import { useEffect, useState } from "react"
import useAppForm from "~/hooks/useAppForm"
import { CampInfoType, campInfoDefaultValues, CampInfoSchema } from "./validation"
import { useCamp } from "~/api2/hooks/useCamp"
import { useMe } from "~/api2/hooks/useMe"

export default function useCampInfoForm() {
  const { campId } = useCampContext()
  const { data: user } = useMe()
  const {
    index: { data: camp, isLoading: isLoadingCamp, isFetching: isFetchingCamp, refetch: refetchCamp },
    update: { mutate: saveCampInfo, isPending: isPendingSaveCampInfo },
  } = useCamp()
  const [defaultValues, setDefaultValues] = useState<CampInfoType>(campInfoDefaultValues)
  const [loadedDone, setLoadedDone] = useState(false)

  const parseSeasonDates = (date?: string) => {
    if (!date) return null
    const parts = date.split("-")
    if (parts.length !== 2) return null

    const parsed = parts.map((num) => {
      const n = parseInt(num, 10)
      return isNaN(n) ? null : String(n)
    })

    return parsed.includes(null) ? null : parsed
  }

  const season_start_num_values = parseSeasonDates(camp?.season_start_mm_dd)
  const season_end_num_values = parseSeasonDates(camp?.season_end_mm_dd)
  const booking_start_num_values = parseSeasonDates(camp?.booking_start_mm_dd)
  const isParkAdminUser = user.email?.includes("@poweredbypark.com") || false

  const reactHookForm = useAppForm<CampInfoType>({
    defaultValues,
    schema: CampInfoSchema,
  })

  const isLoadingData = isLoadingCamp || isFetchingCamp || !camp

  useEffect(() => {
    reactHookForm.reset(defaultValues)
    setLoadedDone((prev) => !prev)
  }, [defaultValues, campId])

  useEffect(() => {
    handleSetDefaultValues()
  }, [campId, camp])

  const handleSetDefaultValues = () => {
    const remoteDefaultValues = {
      name: camp.name,
      primary_color: camp.primary_color,
      secondary_color: camp.secondary_color,
      email: camp.email,
      phone: camp.phone,
      address: camp.address,
      city: camp.city,
      state: camp.state,
      zip: camp.zip,
      bio: camp.bio,
      notification_emails: camp.notification_emails,
      season_start_mm: season_start_num_values?.[0] || null,
      season_start_dd: season_start_num_values?.[1] || null,
      season_end_mm: season_end_num_values?.[0] || null,
      season_end_dd: season_end_num_values?.[1] || null,
      booking_start_mm: booking_start_num_values?.[0] || null,
      booking_start_dd: booking_start_num_values?.[1] || null,
      gate_code: camp.gate_code,
      bathroom_code: camp.bathroom_code,
      laundry_code: camp.laundry_code,
      wifi_network: camp.wifi_network,
      wifi_password: camp.wifi_password,
      allow_same_day_reservations: camp.allow_same_day_reservations,
      allow_rv_on_tent_site: camp.allow_rv_on_tent_site,
      same_day_reservations_cutoff_time: camp.same_day_reservations_cutoff_time,
      stripe_account_id: camp.stripe_account_id,
      slug: camp.slug,
      shortened_slug: camp.shortened_slug,
      timezone_offset: camp.timezone_offset,
      timezone: camp.timezone,
      bookable_for_n_days: camp.bookable_for_n_days,
      date_format: camp.date_format,
      map_url: camp.map_url,
      logo_url: camp.logo_url,
    }
    setDefaultValues(remoteDefaultValues)
  }

  const normalize = (v: string | null | undefined) => (v === "" ? null : v)

  const onSubmit = (data: CampInfoType) => {
    const {
      season_start_mm,
      season_start_dd,
      season_end_mm,
      season_end_dd,
      booking_start_mm,
      booking_start_dd,
      ...rest
    } = data

    const norm_season_start_mm = normalize(season_start_mm)
    const norm_season_start_dd = normalize(season_start_dd)
    const norm_season_end_mm = normalize(season_end_mm)
    const norm_season_end_dd = normalize(season_end_dd)
    const norm_booking_start_mm = normalize(booking_start_mm)
    const norm_booking_start_dd = normalize(booking_start_dd)

    const pad = (v?: string | null) => (v && v.length === 1 ? `0${v}` : (v ?? ""))

    const mergedData = {
      ...rest,
      season_start_mm_dd:
        norm_season_start_mm && norm_season_start_dd
          ? `${pad(norm_season_start_mm)}-${pad(norm_season_start_dd)}`
          : null,
      season_end_mm_dd:
        norm_season_end_mm && norm_season_end_dd
          ? `${pad(norm_season_end_mm)}-${pad(norm_season_end_dd)}`
          : null,
      booking_start_mm_dd:
        norm_booking_start_mm && norm_booking_start_dd
          ? `${pad(norm_booking_start_mm)}-${pad(norm_booking_start_dd)}`
          : null,
    }

    const cleaned: Partial<typeof mergedData> = Object.fromEntries(
      Object.entries(mergedData).filter(([_, v]) => v !== undefined)
    )

    saveCampInfo(cleaned, {
      onSuccess: () => {
        setDefaultValues(data)
        refetchCamp()
      },
    })
  }

  return {
    onSubmit,
    reactHookForm,
    handleSetDefaultValues,
    camp,
    isLoadingData,
    loadedDone,
    isParkAdminUser,
    isPendingSaveCampInfo,
  }
}
